import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import TabsButton from 'components/buttons/TabsButton';
import TabSelect from 'components/forms/basefields/TabSelect';
import TicketForm from './TicketForm';
import TicketSidePanel from './TicketSidePanel';
import { getTicketViewButtons } from './ticketViewButtons';
import TicketInbox from './TicketInbox';
import TicketChat from './TicketChat';
import { useTicketActions } from './useTicketActions';
import { useTicketConversation } from './useTicketConversation';
import { Ticket, TicketInboxView, TicketViewCounts } from './TicketTypes';
import '../../style/scss/detailpage.scss';
import '../../style/scss/tickets.scss';

// The detail page of a ticket with its details, reporter and conversation
const TicketDetail: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const { activeFeatures } = useAuthContext();
    const { setShowFooterLogo } = useGlobalContext();
    const { initializeModal } = useModal();
    const [refreshKey, setRefreshKey] = useState(0);
    const [view, setView] = useState<TicketInboxView>('inbox');
    const [viewCounts, setViewCounts] = useState<TicketViewCounts | null>(null);
    const [pageTop, setPageTop] = useState(0);
    const pageRef = useRef<HTMLDivElement>(null);

    // Hide the footer logo while the page fills the viewport and restore it when leaving
    useEffect(() => {
        setShowFooterLogo(false);
        return () => setShowFooterLogo(true);
        // eslint-disable-next-line
    }, []);

    // Measure the top of the columns so they can fill the rest of the viewport
    useEffect(() => {
        const measure = () => {
            if (pageRef.current) {
                setPageTop(pageRef.current.getBoundingClientRect().top + window.scrollY);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Load the ticket with its conversation
    const { ticket, messages, tasks, loadStatus, refresh } = useTicketConversation(id);

    // Open the first ticket of the inbox column when the page is opened without a ticket
    const handleTicketsLoaded = useCallback((loadedTickets: Ticket[]) => {
        if (!id && loadedTickets.length > 0) {
            history.replace(`/tickets/${loadedTickets[0].id}`);
        }
    }, [id, history]);

    // Refresh the ticket, the conversation and the inbox column after a change
    const refreshAll = useCallback(() => {
        refresh();
        setRefreshKey(key => key + 1);
    }, [refresh]);

    // Refresh the inbox column and the open conversation every half minute while the tab is visible
    useEffect(() => {
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') refreshAll();
        }, 30000);
        return () => window.clearInterval(interval);
    }, [refreshAll]);

    // Open the form to add a new ticket
    const handleAdd = () => {
        initializeModal(<TicketForm />, {
            title: t('button.add_object_label', { object_name: t('ticket.general.object_name.singular') }),
            modalSize: 'add-form'
        });
    };

    // The buttons to switch between the conversation and the list view
    const viewButtons = getTicketViewButtons(history, 'chat');

    // The status tabs of the header with the counts of every view in the color of the status
    const viewTabs = useMemo(() => (['inbox', 'open', 'parked', 'awaiting'] as TicketInboxView[]).map(tab => ({
        value: tab,
        label: tab === 'inbox' ? 'ticket.tabs.inbox_label' : `ticket.status.${tab}`,
        count: viewCounts && viewCounts[tab as keyof TicketViewCounts] > 0 ? viewCounts[tab as keyof TicketViewCounts] : undefined,
        countClass: `ticket-${tab}`,
    })), [viewCounts]);

    // The button bar keeps the width of the lists, which only widen with the extended lists feature
    const headerWidthClass = activeFeatures.includes('extended_lists') ? 'container-wide' : 'container-filled';

    // The delete and switch requester actions of the more menu, a deleted ticket leaves the page
    const { onDelete, onSwitchReporter, onCreateProposal, onCreateJob, onCreateDeal, onStartTimer } = useTicketActions({
        ticketId: id ? parseInt(id) : null,
        ticket,
        onDeleted: () => { history.push('/tickets'); setRefreshKey(key => key + 1); },
        onChanged: refreshAll,
    });

    return (
        <div className='detailpage ticket-page'>
            <div className={`${headerWidthClass} ticket-page-header`}>
                <PrimaryButton
                    label={t('button.plus_add_object_label', { object_name: t('ticket.general.object_name.singular') })}
                    size='small'
                    onlyViewRestriction={true}
                    onClick={handleAdd}
                />
                <div className='ticket-page-header-right'>
                    <TabSelect
                        options={viewTabs}
                        selectedOption={viewTabs.find(tab => tab.value === view)}
                        onChange={(value) => setView(value as TicketInboxView)}
                        optionFormat='label'
                    />
                    <TabsButton
                        buttons={viewButtons.buttons}
                        currentTabValue={viewButtons.currentView}
                        onTabsClick={viewButtons.onViewChange}
                        size='small'
                    />
                </div>
            </div>
            <div className='ticket-columns' ref={pageRef} style={{ height: `calc(100vh - ${pageTop}px - 1rem)` }}>
            <TicketInbox activeTicketId={id ? parseInt(id) : null} refreshKey={refreshKey} view={view} onTicketsLoaded={handleTicketsLoaded} onCountsLoaded={setViewCounts} />

            {ticket ? (
                <TicketChat ticket={ticket} messages={messages} tasks={tasks} onRefresh={refreshAll}
                            onSwitchReporter={onSwitchReporter} onDelete={onDelete}
                            onCreateProposal={onCreateProposal} onCreateJob={onCreateJob} onCreateDeal={onCreateDeal} onStartTimer={onStartTimer} />
            ) : id ? (
                <div className='ticket-chat'><div className='loader' /></div>
            ) : (
                <div className='ticket-chat'>
                    <p className='ticket-chat-empty'>{t('ticket.general.no_ticket_selected_message')}</p>
                </div>
            )}

            {ticket ? (
                <TicketSidePanel ticket={ticket} loadStatus={loadStatus} refetchData={refreshAll} />
            ) : (
                <div className='ticket-side'>
                    <div className='card ticket-side-empty' />
                </div>
            )}
            </div>
        </div>
    );
};

export default TicketDetail;
